import React, { useEffect, useMemo, useState } from 'react';
import InfoTip from '../InfoTip';
import { Check, ChevronLeft, ChevronRight, TriangleAlert } from 'lucide-react';
import { apiService } from '../../services/api';
import JiraKeyChip from '../JiraKeyChip';
import EpicSummaryPanel from '../epics/EpicSummaryPanel';
import { EpicTarget } from '../epics/types';
import ProgressBar from './ProgressBar';
import { QuarterData, QuarterEpic, QuarterOption, QuarterProject, epicTitle, plural } from './types';
import { useBoundaries } from '../../hooks/useBoundaries';
import * as fmt from '../../lib/format';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const CAPTION = 'text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';
const ICON_BUTTON =
  'rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

interface Props {
  /** Null until the person picks one: the current quarter is used. */
  quarterId: string | null;
  onQuarter: (id: string) => void;
  projectKey: string | null;
  onProject: (key: string | null) => void;
  refreshKey: number;
}

// ─── The quarter at a glance ────────────────────────────────────────────────────

function Checkpoint({ data }: { data: QuarterData }) {
  const cp = data.checkpoint;
  const mid = data.overall.at_checkpoint;
  const judged = cp.reached && mid && mid.shipped_or_ready_pct !== null;
  return (
    <div className="min-w-[14rem] max-w-sm">
      <div className="flex items-center">
        <h3 className={CAPTION}>Halfway check · {fmt.dayMonth(cp.day)}</h3>
        <InfoTip id="planning.checkpoint" />
      </div>
      {judged ? (
        <>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.percent(mid!.shipped_or_ready_pct)}</span>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                mid!.meets_target ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-amber-50 text-amber-900 ring-amber-200'
              }`}
            >
              {mid!.meets_target ? <Check className="h-3 w-3" aria-hidden /> : <TriangleAlert className="h-3 w-3" aria-hidden />}
              {mid!.meets_target ? `Met the ${fmt.percent(cp.target_pct)} target` : `Below the ${fmt.percent(cp.target_pct)} target`}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">Shipped or release-ready at the end of Sprint {cp.after_sprint}, of {fmt.count(mid!.tickets)} tickets then.</p>
        </>
      ) : (
        <p className="mt-1 text-sm text-gray-600">
          Target {fmt.percent(cp.target_pct)} shipped or release-ready by the end of Sprint {cp.after_sprint}.
          {!cp.reached && ' Not reached yet.'}
        </p>
      )}
    </div>
  );
}

function Headline({ data }: { data: QuarterData }) {
  const o = data.overall;
  const counted = o.shipped + o.ready + o.open;
  return (
    <section className={`${CARD} mb-6 p-5`} aria-label={`${data.quarter.label} progress`}>
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
        <div className="min-w-0">
          <div className="flex items-center">
            <h2 className={CAPTION}>{data.quarter.label} goals</h2>
            <InfoTip id="planning.goal-epics" />
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-4xl font-semibold tabular-nums tracking-tight text-gray-900">{fmt.percent(o.shipped_or_ready_pct)}</span>
            <span className="text-sm text-gray-600">of tickets shipped or release-ready<InfoTip id="planning.shipped-ready" className="ml-1" /></span>
          </div>
          <p className="mt-1.5 text-sm text-gray-600">
            {fmt.count(o.shipped + o.ready)} of {fmt.count(counted)} tickets: {fmt.count(o.shipped)} shipped, {fmt.count(o.ready)} release-ready,{' '}
            {fmt.count(o.open)} still open · {o.epics} goal {plural(o.epics, 'epic', 'epics')}
          </p>
          {o.invalid > 0 && <p className="mt-0.5 text-xs text-gray-500">{fmt.count(o.invalid)} closed as invalid are left out.</p>}
          {data.quarter.phase === 'past' && <p className="mt-0.5 text-xs text-gray-500">As it stood when the quarter ended.</p>}
        </div>
        <Checkpoint data={data} />
      </div>
      <div className="mt-5">
        <ProgressBar shipped={o.shipped} ready={o.ready} open={o.open} invalid={o.invalid} height="h-2.5" marker={data.checkpoint.target_pct} markerLabel={`Halfway target ${data.checkpoint.target_pct}%`} />
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-600" aria-hidden />Shipped</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-300" aria-hidden />Release-ready</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-gray-300" aria-hidden />Still open</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 rounded bg-gray-900" aria-hidden />{fmt.percent(data.checkpoint.target_pct)} target</span>
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ row, selected, onSelect }: { row: QuarterProject; selected: boolean; onSelect: (key: string | null) => void }) {
  const mid = row.at_checkpoint;
  return (
    <button
      type="button"
      onClick={() => onSelect(selected ? null : row.project_key)}
      aria-pressed={selected}
      className={`group w-full rounded-2xl bg-white p-4 text-left shadow-sm ring-1 transition-[transform,box-shadow,ring-color] duration-150 ease-out hover:shadow-md active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 contrast-more:ring-gray-400 ${
        selected ? 'ring-2 ring-blue-500' : 'ring-gray-200/70 hover:ring-gray-300'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold uppercase tracking-wider text-gray-500">{row.project_key}</div>
          <div className="truncate text-sm text-gray-600">{row.project_name ?? ''}</div>
        </div>
        <div className="text-2xl font-bold tabular-nums tracking-tight text-gray-900">{fmt.percent(row.shipped_or_ready_pct)}</div>
      </div>
      <div className="mt-3">
        <ProgressBar shipped={row.shipped} ready={row.ready} open={row.open} invalid={row.invalid} />
      </div>
      <div className="mt-2 text-xs text-gray-500">
        {row.epics} goal {plural(row.epics, 'epic', 'epics')} · {fmt.count(row.tickets)} {plural(row.tickets, 'ticket', 'tickets')}
      </div>
      {mid && mid.shipped_or_ready_pct !== null && (
        <div className="mt-0.5 text-xs text-gray-500">At the halfway check {fmt.percent(mid.shipped_or_ready_pct)}</div>
      )}
    </button>
  );
}

// ─── The goal epics ─────────────────────────────────────────────────────────────

function EpicRow({ epic, onOpen }: { epic: QuarterEpic; onOpen: (epic: QuarterEpic) => void }) {
  const mid = epic.at_checkpoint;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(epic)}
        className="flex w-full items-start justify-between gap-4 px-5 py-3.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span role="presentation" onClick={(e) => e.stopPropagation()}>
              <JiraKeyChip issueKey={epic.issue_key} url={epic.jira_url} />
            </span>
            <span className="text-xs text-gray-500">{epic.project_key}</span>
          </div>
          <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900">{epicTitle(epic.name)}</p>
          <div className="mt-2.5 max-w-md">
            <ProgressBar shipped={epic.shipped} ready={epic.ready} open={epic.open} invalid={epic.invalid} />
          </div>
          <p className="mt-1.5 text-xs text-gray-500">
            {epic.tickets === 0
              ? 'No tickets yet'
              : `${epic.shipped} shipped · ${epic.ready} release-ready · ${epic.open} open${epic.invalid > 0 ? ` · ${epic.invalid} invalid` : ''}`}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-lg font-semibold tabular-nums tracking-tight text-gray-900">{fmt.percent(epic.shipped_or_ready_pct)}</div>
          {mid && mid.shipped_or_ready_pct !== null && <div className="text-xs tabular-nums text-gray-500">{fmt.percent(mid.shipped_or_ready_pct)} at the halfway check</div>}
        </div>
      </button>
    </li>
  );
}

// ─── The Quarter tab ────────────────────────────────────────────────────────────

export default function QuarterView({ quarterId, onQuarter, projectKey, onProject, refreshKey }: Props) {
  const boundaries = useBoundaries();
  const [options, setOptions] = useState<QuarterOption[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [data, setData] = useState<QuarterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [target, setTarget] = useState<EpicTarget | null>(null);

  const activeId = quarterId ?? currentId;

  useEffect(() => {
    let cancelled = false;
    apiService
      .getPlanningQuarters()
      .then((r) => {
        if (cancelled || !r.success) return;
        setOptions(r.data ?? []);
        setCurrentId(r.meta?.current ?? null);
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the quarters'));
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setLoading(true);
    apiService
      .getPlanningQuarter(activeId)
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setData(r.data);
          setError(null);
        } else setError(r.error || 'Could not load the quarter');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the quarter'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeId, refreshKey]);

  const index = options.findIndex((o) => o.id === activeId);
  const older = index >= 0 ? options[index + 1] : undefined;
  const newer = index > 0 ? options[index - 1] : undefined;

  const epics = useMemo(() => (data?.epics ?? []).filter((e) => !projectKey || e.project_key === projectKey), [data, projectKey]);

  const openEpic = (epic: QuarterEpic) => {
    if (!data) return;
    // Finishing a text selection inside a row must not open the panel.
    if (window.getSelection?.()?.toString()) return;
    setTarget({
      issueKey: epic.issue_key,
      projectName: epic.project_name ?? epic.project_key,
      title: epicTitle(epic.name),
      from: data.quarter.range.from,
      to: data.quarter.range.to,
      periodLabel: data.quarter.label,
    });
    setPanelOpen(true);
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous quarter" disabled={!older} onClick={() => older && onQuarter(older.id)} className={ICON_BUTTON}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <label className="sr-only" htmlFor="planning-quarter">Quarter</label>
          <select
            id="planning-quarter"
            value={activeId ?? ''}
            onChange={(e) => onQuarter(e.target.value)}
            className="rounded-lg bg-white py-1.5 pl-3 pr-9 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 contrast-more:ring-gray-400"
          >
            {(() => {
              const optionOf = (o: (typeof options)[number]) => (
                <option key={o.id} value={o.id}>
                  {o.label}{o.phase === 'current' ? ' · current' : o.phase === 'future' ? ' · ahead' : ''}
                </option>
              );
              // The quarters before the Jira conventions started are a group of their own.
              const earlier = boundaries ? options.filter((o) => o.id < boundaries.quarter) : [];
              if (!boundaries || earlier.length === 0 || earlier.length === options.length) return options.map(optionOf);
              return (
                <>
                  <optgroup label={boundaries.fromLabel}>{options.filter((o) => o.id >= boundaries.quarter).map(optionOf)}</optgroup>
                  <optgroup label={boundaries.label}>{earlier.map(optionOf)}</optgroup>
                </>
              );
            })()}
          </select>
          <button type="button" aria-label="Next quarter" disabled={!newer} onClick={() => newer && onQuarter(newer.id)} className={ICON_BUTTON}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {projectKey && (
          <button type="button" onClick={() => onProject(null)} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-500 transition-colors active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            {projectKey}
            <span aria-hidden>×</span>
            <span className="sr-only">Clear the project filter</span>
          </button>
        )}
      </div>

      {error && <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}

      {loading && !data ? (
        <div role="status" aria-label="Loading the quarter">
          <div className={`${CARD} mb-6 h-44 animate-pulse motion-reduce:animate-none`} />
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-2xl bg-white ring-1 ring-gray-200/70 motion-reduce:animate-none" />)}
          </div>
        </div>
      ) : data ? (
        <>
          {data.overall.epics === 0 ? (
            <div className={`${CARD} px-6 py-14 text-center`}>
              <p className="text-sm text-gray-700">No quarter goals are named for {data.quarter.label} yet.</p>
              <p className="mt-1 text-xs text-gray-500">A quarter goal is an epic named like “[{data.quarter.label}] Team – Goal”.</p>
            </div>
          ) : (
            <>
              <Headline data={data} />
              <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {data.projects.map((p) => (
                  <ProjectCard key={p.project_key} row={p} selected={projectKey === p.project_key} onSelect={onProject} />
                ))}
              </div>

              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  Quarter goals{projectKey ? ` · ${projectKey}` : ''} · {fmt.count(epics.length)}
                </h2>
                <div className="text-xs text-gray-500">Furthest behind first · open one for its summary</div>
              </div>
              <ul className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
                {epics.map((e) => <EpicRow key={e.issue_key} epic={e} onOpen={openEpic} />)}
              </ul>
            </>
          )}
        </>
      ) : null}

      <EpicSummaryPanel isOpen={panelOpen} target={target} onClose={() => setPanelOpen(false)} />
    </div>
  );
}
